import React, { useEffect, useMemo, useState } from "react";
import { RiCloseCircleFill } from "react-icons/ri";
import { FiChevronLeft } from "react-icons/fi";
import { format } from "date-fns";

import { getEmailReply, getMailHistory } from "../../API";
import ContentLoader from "../ContentLoader";
import classes from "./style.module.css";
import { maskEmail } from "../../Functions/mask";
import EmailReplyModal from "./EmailReplyModal";

export default function EmailHistory({ show, setShow = () => { }, customerId = "", isMasked = false, borrowerEmail = "", showReplyButton = true }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showEmailReply, setShowEmailReply] = useState(false);
  const [emailReply, setEmailReply] = useState(null);
  const [replyLoading, setReplyLoading] = useState(false);
  const [showReplyOnly, setShowReplyOnly] = useState(false);
  const [showEmailReplyModal, setShowEmailReplyModal] = useState(false);

  const removeSelectedEmail = () => {
    setSelectedEmail(null);
  };

  const closeModal = () => {
    setShow(false);
    setReplyLoading(false);
    setShowReplyOnly(false);
    removeSelectedEmail();
  };

  const fetchEmailReply = async () => {
    if (!selectedEmail?.to_mail || !selectedEmail?.ticket) return;

    setReplyLoading(true);
    const res = await getEmailReply({
      mail: selectedEmail?.to_mail,
      ticket_id: selectedEmail?.ticket,
    });
    if (res?.data) {
      setEmailReply(res?.data?.reply);
    } else setEmailReply(null);

    setReplyLoading(false);
  };

  useEffect(() => {
    if (!!selectedEmail) fetchEmailReply();
    setShowEmailReply(false);
  }, [selectedEmail]);

  const fetchDetails = async () => {
    if (!customerId) return;
    setLoading(true);

    const res = await getMailHistory({
      customer_id: customerId,
      type: "prelitigation",
    });

    if (res) {
      setData(res?.data);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!!customerId && !!show) fetchDetails();
  }, [customerId, show]);

  const filterData = useMemo(() => {
    if (!showReplyOnly) return data;
    return data?.filter((item) => !!item?.ticket && item?.ticket !== "null");
  }, [data, showReplyOnly]);

  return (
    <>
      <div onClick={() => closeModal()} className={`${classes.wrapper} ${show ? classes.show : classes.hidden}`} />

      <div className={`${classes.sidepanel} ${show ? classes.show : classes.hidden}`}>
        <div className={classes.sidepanel_header}>
          <div className="d-flex align-items-center gap-3">
            <p>Email History</p>

            <label style={{ cursor: "pointer" }} className="d-flex align-items-center gap-2">
              <input type="checkbox" checked={showReplyOnly} onChange={(e) => setShowReplyOnly(e.target.checked)} />
              <span style={{ userSelect: "none" }}>Show with Replies only</span>
            </label>
          </div>
          <button onClick={() => closeModal()}>
            <RiCloseCircleFill />
          </button>
        </div>
        <div className={classes.scroll_content}>
          {!!filterData?.length && !loading && (
            <div className={classes.email_list_wrapper}>
              {filterData?.map((item, index) => (
                <div key={index} className={classes.email_wrapper} onClick={() => setSelectedEmail(item)} style={{ backgroundColor: item?.owner === "user" ? "#e8f0fe" : "transparent" }}>
                  <div className={classes.email_avatar}>{item?.from_mail?.slice(0, 1) || "M"}</div>
                  <div className={classes.email_content}>
                    <div className={classes.email_from_wrapper}>
                      <p className={classes.email_from}>
                        {item?.from_mail ?? "From"}
                        <br />
                        <small>To : {isMasked ? maskEmail(item?.to_mail) : maskEmail(item?.to_mail)}</small>
                      </p>
                      <p className={classes.timestamp}>{!!item?.created_on ? format(new Date(item?.created_on), "do MMM yyyy") : "DD MM YYYY"}</p>
                    </div>
                    <p className={classes.subject}>{item?.subject}</p>

                  </div>
                </div>
              ))}
            </div>
          )}

          {!data?.length && !loading && (
            <div>
              <p style={{ textAlign: "center", padding: "20px" }}>No Email History found</p>
            </div>
          )}

          {!!data?.length && !filterData?.length && !loading && (
            <div>
              <p style={{ textAlign: "center", padding: "20px" }}>No Email with a reply found.</p>
            </div>
          )}

          {loading && <ContentLoader />}
        </div>

        {/* Fixed Send Email Reply Button in Main List View */}
        {showReplyButton && (
          <div className={classes.fixed_reply_button_container}>
            <button
              className={classes.fixed_reply_button}
              onClick={() => setShowEmailReplyModal(true)}
            >
              Send Email Reply
            </button>
          </div>

        )}
      </div>

      {/* Email Thread View */}
      <div onClick={() => removeSelectedEmail()} className={`${classes.wrapper} ${!!selectedEmail ? classes.show : classes.hidden}`} />

      <div className={`${classes.sidepanel} ${!!selectedEmail ? classes.show : classes.hidden}`}>
        <div className={classes.thread_header}>
          <button className={classes.back_button} onClick={() => removeSelectedEmail()}>
            <FiChevronLeft />
          </button>


        </div>

        <div className={classes.thread_scroll_content}>
          <p className={classes.thread_subject}>{selectedEmail?.subject}</p>

          {!!selectedEmail && (
            <>
              <div
                onClick={() => (!!selectedEmail?.ticket && selectedEmail?.ticket !== "null" ? setShowEmailReply((prev) => !prev) : {})}
                className={classes.thread_wrapper}
              >
                <div className={classes.email_avatar}>{selectedEmail?.from_mail?.slice(0, 1) || "M"}</div>
                <div>
                  <div className={classes.mail_id_timestamp}>
                    <p className={classes.thread_email_from}>{selectedEmail?.from_mail || "From"}</p>
                    <p className={classes.thread_timestamp}>
                      {!!selectedEmail?.created_on ? format(new Date(selectedEmail?.created_on), "do MMM yyyy") : "DD MM YYYY"}
                    </p>
                  </div>
                  <p className={classes.thread_email_from}>To: {isMasked ? maskEmail(selectedEmail?.to_mail) : maskEmail(selectedEmail?.to_mail)}</p>
                </div>
              </div>

              <div className={`${classes.email_body} ${showEmailReply && classes.hide_email_body}`}>
                <iframe srcDoc={selectedEmail?.body} seamless="seamless">
                  <p>{selectedEmail?.body}</p>
                </iframe>
              </div>

              {/* Email Reply here */}
              {!!selectedEmail?.ticket && selectedEmail?.ticket !== "null" && (
                <>
                  {replyLoading && <div className={classes.loading}>Loading...</div>}
                  {!!emailReply && !replyLoading && (
                    <>
                      <div className={classes.reply_wrapper}>
                        <div className={classes.reply_email_avatar}>{selectedEmail?.to_mail?.slice(0, 1) || "M"}</div>
                        <div>
                          <p className={classes.thread_email_from}>
                            {!!selectedEmail?.to_mail ? (isMasked ? maskEmail(selectedEmail?.to_mail) : selectedEmail?.to_mail) : "Customer"}
                          </p>
                          <p className={classes.thread_timestamp}>Replied</p>
                        </div>
                      </div>

                      <div className={classes.email_body}>
                        <iframe srcDoc={emailReply} seamless="seamless">
                          <p>{emailReply}</p>
                        </iframe>
                      </div>
                    </>
                  )}
                  {!emailReply && !replyLoading && (
                    <div>
                      <p style={{ textAlign: "center", padding: "20px" }}>No Reply Found</p>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {!selectedEmail && (
            <div>
              <p style={{ textAlign: "center", padding: "20px" }}>No Email Selected</p>
            </div>
          )}
        </div>
      </div>

      {/* Email Reply Modal */}
      <EmailReplyModal
        show={showEmailReplyModal}
        setShow={setShowEmailReplyModal}
        selectedEmail={selectedEmail}
        borrowerEmail={borrowerEmail}
        emailData={data}
      />
    </>
  );
}
